import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { User, AppState, Transaction, Counterparty, DocType, GeneratedDocument, CompanyProfile, ALL_ORGS } from './types';
import { api, AUTH_EXPIRED_EVENT } from './services/api'; 
import { Login } from './pages/Login';
import { Operations } from './pages/Operations';
import { Directories } from './pages/Directories';
import { Reports } from './pages/Reports';
import { Profile } from './pages/Profile';
import { Layout } from './components/Layout';
import { Toast } from './components/UI';
import { TaxCalendarPage } from './pages/TaxCalendar';
import { Projects, ProjectCard } from './pages/Projects';
import { Planning } from './pages/Planning';
import { ImportPage } from './pages/Import';
import { Volumes } from './pages/Volumes';
import Analysis from './pages/Analysis';
import { confirmDialog } from './components/Dialogs';

interface AuthContextType {
  user: User | null;
  // Возвращают текст ошибки или null при успехе
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => void;
}
const AuthContext = createContext<AuthContextType>({} as any);
export const useAuth = () => useContext(AuthContext);

interface DataContextType {
  state: AppState;
  refreshData: () => void;
  // Растёт после каждого успешного сохранения на сервере — сигнал перезапросить отчёты
  revision: number;
  // organizationId — обязательно в режиме «Все организации», иначе берётся выбранная
  // Возвращает id сохранённой операции (null — не сохранилась)
  addTransaction: (t: NewTransaction) => Promise<string | null>;
  // Несколько операций разом (массовая выплата зарплаты): одно обновление данных в конце
  addTransactions: (list: NewTransaction[]) => Promise<void>;
  updateTransaction: (id: string, updates: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;
  addCounterparty: (c: Omit<Counterparty, 'id'>) => Promise<Counterparty | null>;
  updateCounterparty: (id: string, updates: Partial<Counterparty>) => void;
  // Обновить карточку в справочнике без перезагрузки (ответ сервера: статус НПД и т. п.)
  patchCounterparty: (id: string, updates: Partial<Counterparty>) => void;
  deleteCounterparty: (id: string) => void;
  addOrganization: (org: Omit<CompanyProfile, 'id'>) => void;
  updateOrganization: (org: CompanyProfile) => void;
  deleteOrganization: (id: string) => void; // <-- ДОБАВЛЕНО
  switchOrganization: (id: string) => void;
  addUser: (u: { name: string; email: string; password: string; role: string }) => void;
  deleteUser: (id: string) => void;
  generateDocs: (tId: string, types: DocType[]) => void;
  showNotification: (msg: string, type: 'success' | 'error') => void;
}
const DataContext = createContext<DataContextType>({} as any);
export const useData = () => useContext(DataContext);

// id создаётся на клиенте: к времени добавляем случайный хвост — за одну миллисекунду бывает несколько операций
export type NewTransaction = Omit<Transaction, 'id' | 'documents' | 'organizationId'> & { organizationId?: string };

// Выбранная в шапке организация: источник правды — state контекста, localStorage только
// восстанавливает выбор после перезагрузки страницы
const ORG_KEY = 'currentOrgId';
const readSavedOrg = () => { try { return localStorage.getItem(ORG_KEY) || ''; } catch { return ''; } };
const saveOrg = (id: string) => { try { if (id) localStorage.setItem(ORG_KEY, id); } catch { /* не критично */ } };

const newTxId = () => `t${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const App: React.FC = () => {
  const [state, setState] = useState<AppState>({
    user: null,
    users: [],
    counterparties: [],
    transactions: [],
    organizations: [], 
    currentOrganizationId: readSavedOrg()
  });

  const [user, setUser] = useState<User | null>(null);
  // Пока не спросили сервер о текущей сессии, ничего не рендерим (иначе мелькнёт экран входа)
  const [authChecked, setAuthChecked] = useState(false);
  const [revision, setRevision] = useState(0);
  const bumpRevision = () => setRevision(r => r + 1);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), type === 'error' ? 8000 : 3000);  // ошибку — дольше, чтобы успеть прочитать
  };

  // Выбор хранится и у пользователя на сервере: он берётся при входе и когда в этом браузере выбора ещё нет.
  // После перезагрузки приоритет у выбора этого браузера — сервер не перебивает его
  const applyUser = (u: User | null, preferServer: boolean) => {
    setUser(u);
    const serverScope = u?.orgScope;
    if (serverScope) setState(prev => (preferServer || !prev.currentOrganizationId) && prev.currentOrganizationId !== serverScope
        ? { ...prev, currentOrganizationId: serverScope } : prev);
  };

  // Единственная запись выбора в localStorage
  useEffect(() => { saveOrg(state.currentOrganizationId); }, [state.currentOrganizationId]);

  useEffect(() => {
    api.me().then(u => applyUser(u, false)).catch(() => setUser(null)).finally(() => setAuthChecked(true));
    const onExpired = () => setUser(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // Номер последней загрузки: ответ более ранней загрузки, пришедший позже, отбрасывается
  const loadSeq = useRef(0);
  const scopeQueue = useRef<Promise<unknown>>(Promise.resolve());
  const refreshData = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
        // Операции всех организаций аккаунта: выбранная организация — фильтр на клиенте,
        // поэтому переключение (в т.ч. «Все организации») мгновенное и без перезагрузки. Справочники — общие
        const [orgsDB, txs, cps, usersDB] = await Promise.all([
            api.getOrganizations(),
            api.getTransactions(ALL_ORGS),
            api.getCounterparties(),
            api.getUsers()
        ]);
        if (seq !== loadSeq.current) return;

        setState(prev => {
            // Выбор не трогаем; только если выбранной организации больше нет — берём первую
            const cur = prev.currentOrganizationId;
            const valid = cur === ALL_ORGS ? orgsDB.length > 1 : orgsDB.some(o => o.id === cur);
            return {
                ...prev,
                organizations: orgsDB,
                currentOrganizationId: valid || orgsDB.length === 0 ? cur : orgsDB[0].id,
                transactions: txs,
                counterparties: cps,
                users: usersDB
            };
        });
        // Сводки (дашборд, налоги, реализации) перезапрашиваются по revision — иначе после оплаты счёта цифры старые
        setRevision(r => r + 1);
    } catch (error) {
        console.error("Ошибка загрузки данных:", error);
    }
  }, []);
  const userId = user?.id;
  useEffect(() => {
    if (userId) refreshData();
  }, [refreshData, userId]);

  const authValues = {
    user,
    login: async (email: string, password: string) => {
      try {
          applyUser(await api.login(email, password), true);
          return null;
      } catch (e: any) {
          return e?.message || 'Ошибка входа';
      }
    },
    logout: async () => {
      try { await api.logout(); } catch { /* сессии уже нет */ }
      setUser(null);
      setState(prev => ({ ...prev, users: [], counterparties: [], transactions: [], organizations: [] }));
    }
  };

  const dataValues = {
    state,
    refreshData,
    revision,
    showNotification,
    
    addTransaction: async (t: NewTransaction) => {
      const newTx: Transaction = {
        ...t,
        id: newTxId(),
        organizationId: t.organizationId || state.currentOrganizationId,
        date: t.date || new Date().toISOString().split('T')[0], 
        documents: [],
        items: t.items || [],
        
        // ИСПРАВЛЕНИЕ: Используем 'user' (активный вход), а не 'state.user' (который может быть пустым)
        userId: user?.id, 
      };
      
      setState(prev => ({ ...prev, transactions: [newTx, ...prev.transactions] }));

      try {
        await api.addTransaction(newTx);
        bumpRevision();
        refreshData(); // сервер мог создать налоговые платежи по выплате
        // showNotification('Операция создана'); // (Вы просили убрать уведомление)
        return newTx.id;
      } catch (e) {
        showNotification('Ошибка сохранения', 'error');
        refreshData();
        return null;
      }
    },

    addTransactions: async (list: NewTransaction[]) => {
      try {
        for (const t of list) {
          await api.addTransaction({ ...t, id: newTxId(), organizationId: t.organizationId || state.currentOrganizationId,
                                     documents: [], items: t.items || [], userId: user?.id } as Transaction);
        }
      } catch (e) {
        showNotification('Ошибка сохранения', 'error');
      }
      bumpRevision();
      refreshData();
    },

    updateTransaction: async (id: string, updates: Partial<Transaction>) => {
       setState(prev => ({
        ...prev,
        transactions: prev.transactions.map(t => t.id === id ? { ...t, ...updates } : t)
       }));

       try {
           await api.updateTransaction(id, updates);
           // Снятие пометки «проверить» ничего больше не меняет — без полной перезагрузки операций
           if (Object.keys(updates).every(k => k === 'reviewNote')) bumpRevision();
           else refreshData(); // и пересчитать платежи по выплатам
       } catch (e) {
           showNotification('Ошибка обновления', 'error');
           refreshData();
       }
    },

    deleteTransaction: async (id: string) => {
      setState(prev => ({
          ...prev,
          transactions: prev.transactions.filter(t => t.id !== id)
      }));

      try {
        await api.deleteTransaction(id);
        bumpRevision();
        refreshData();
      } catch (e) {
        showNotification('Ошибка удаления', 'error');
        refreshData();
      }
    },

    addCounterparty: async (c: Omit<Counterparty, 'id'>) => {
      const newC: Counterparty = { ...c, id: `c${Date.now()}`, files: [] };
      try {
        await api.addCounterparty(newC);
        refreshData();
        showNotification('Контрагент добавлен');
        return newC;
      } catch (e) {
        showNotification('Ошибка добавления', 'error');
        return null;
      }
    },

    updateCounterparty: async (id: string, updates: Partial<Counterparty>) => {
        const current = state.counterparties.find(c => c.id === id);
        if (!current) return;
        const next = { ...current, ...updates };
        setState(prev => ({ ...prev, counterparties: prev.counterparties.map(c => c.id === id ? next : c) }));
        try {
            const saved = await api.updateCounterparty(next);
            setState(prev => ({ ...prev, counterparties: prev.counterparties.map(c => c.id === id ? { ...c, ...saved } : c) }));
            bumpRevision();  // карточка: договоры, статус НПД, «бывший сотрудник» — по новому ИНН
            showNotification('Сохранено');
        } catch (e: any) {
            showNotification(e?.message ? `Не сохранено: ${e.message}` : 'Ошибка сохранения', 'error');
            refreshData();
        }
    },

    patchCounterparty: (id: string, updates: Partial<Counterparty>) =>
        setState(prev => ({ ...prev, counterparties: prev.counterparties.map(c => c.id === id ? { ...c, ...updates } : c) })),

    // В архив (25.10.2026): карточка, операции и документы сохраняются; вернуть — Справочники → «Архив»
    deleteCounterparty: async (id: string) => {
        const cp = state.counterparties.find(c => c.id === id);
        if (!(await confirmDialog(`Перенести «${cp?.name || 'контрагента'}» в архив? Операции и документы сохранятся, вернуть можно во вкладке «Архив».`))) return;
        try {
            await api.deleteCounterparty(id);
            setState(prev => ({ ...prev, counterparties: prev.counterparties.map(c => c.id === id ? { ...c, archivedAt: new Date().toISOString() } : c) }));
            showNotification('Перенесено в архив');
        } catch (e: any) {
            showNotification(e?.message || 'Ошибка', 'error');
        }
    },

    addOrganization: async (orgData: Omit<CompanyProfile, 'id'>) => {
        const newId = `org_${Date.now()}`;
        const newOrg: CompanyProfile = { ...orgData, id: newId, accounts: [], activeAccountId: null };
        try {
            await api.saveOrganization(newOrg);
            api.setScope(newId).catch(() => { /* выбор всё равно сохранён в браузере */ });
            setState(prev => ({
                ...prev,
                organizations: [...prev.organizations, newOrg],
                currentOrganizationId: newId 
            }));
            refreshData();
            showNotification('Организация создана');
        } catch (e) { showNotification('Ошибка', 'error'); }
    },

    updateOrganization: async (org: CompanyProfile) => {
      try {
          // ИСПОЛЬЗУЕМ НОВЫЙ МЕТОД (PUT)
          await api.updateOrganization(org); 
          bumpRevision();
          
          // Обновляем состояние локально, чтобы сразу увидеть изменения
          setState(prev => ({
              ...prev,
              organizations: prev.organizations.map(o => o.id === org.id ? org : o)
          }));
          
          refreshData();
          showNotification('Настройки сохранены', 'success');
      } catch (e: any) { 
          showNotification(e?.message ? `Не сохранено: ${e.message}` : 'Ошибка при обновлении', 'error'); 
      }
    },
    
    deleteOrganization: async (id: string) => {
        if (state.organizations.length <= 1) {
            showNotification('Нельзя удалить последнюю организацию', 'error');
            return;
        }
        if (!(await confirmDialog('Удалить организацию и все данные?'))) return;

        try {
            await api.deleteOrganization(id);
            
            setState(prev => {
                const remainingOrgs = prev.organizations.filter(o => o.id !== id);
                const cur = prev.currentOrganizationId;
                const lost = cur === id || (cur === ALL_ORGS && remainingOrgs.length < 2);
                return { ...prev, organizations: remainingOrgs, currentOrganizationId: lost ? remainingOrgs[0].id : cur };
            });
            refreshData();
            showNotification('Организация удалена');

        } catch (e) {
            showNotification('Ошибка удаления', 'error');
        }
    },

    // Единственное место, где выбор меняется по действию пользователя
    switchOrganization: (id: string) => {
        setState(prev => prev.currentOrganizationId === id ? prev : { ...prev, currentOrganizationId: id });
        // Запросы на сервер — по очереди, чтобы при быстром переключении там остался последний выбор
        scopeQueue.current = scopeQueue.current.then(() => api.setScope(id)).catch(() => { /* выбор сохранён в браузере */ });
    },

    addUser: async (u: { name: string; email: string; password: string; role: string }) => {
      try {
          await api.addUser(u);
          refreshData();
          showNotification('Пользователь создан');
      } catch (e: any) { showNotification(e?.message || 'Ошибка', 'error'); }
    },

    deleteUser: async (id: string) => {
       try {
           await api.deleteUser(id);
           refreshData();
           showNotification('Пользователь удален');
       } catch (e) { showNotification('Ошибка', 'error'); }
    },

    generateDocs: async (tId: string, types: DocType[]) => {
      const tempDocs: GeneratedDocument[] = types.map((type, idx) => ({
        id: `temp_${Date.now()}_${idx}`,
        type,
        fileName: '', 
        dateCreated: new Date().toISOString(),
        isOriginalUploaded: false
      }));

      setState(prev => ({
        ...prev,
        transactions: prev.transactions.map(t => 
          t.id === tId ? { ...t, documents: [...t.documents, ...tempDocs] } : t
        )
      }));

      try {
          for (const docPayload of tempDocs) {
              const realPayload = { ...docPayload, id: `d${Date.now()}`, transactionId: tId };
              const savedDoc = await api.generateDocument(realPayload);
              
              const downloadUrl = api.fileUrl(savedDoc.fileName);
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.setAttribute('download', savedDoc.fileName);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
          }
          refreshData();
          
      } catch (e) {
          showNotification('Ошибка генерации', 'error');
          refreshData();
      }
    }
  };

  if (!authChecked) return null;

  return (
    <AuthContext.Provider value={authValues}>
      <DataContext.Provider value={dataValues}>
        <HashRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Operations /></ProtectedRoute>} />
            <Route path="/directories" element={<ProtectedRoute><Directories /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><TaxCalendarPage /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute><ProjectCard /></ProtectedRoute>} />
            <Route path="/planning" element={<ProtectedRoute><Planning /></ProtectedRoute>} />
            <Route path="/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
            <Route path="/analysis" element={<Navigate to="/profile?tab=analysis" replace />} />
            <Route path="/volumes" element={<ProtectedRoute><Volumes /></ProtectedRoute>} />
          </Routes>
        </HashRouter>
        
        {notification && (
            <Toast 
                message={notification.message} 
                type={notification.type} 
                onClose={() => setNotification(null)} 
            />
        )}

      </DataContext.Provider>
    </AuthContext.Provider>
  );
};

export default App;